/**
 * Orquestación de `/activar/{token}` (T033): **verificación del documento** (005 · QA-6) →
 * bienvenida → aviso de privacidad → siguiente paso.
 *
 * Vive fuera del archivo de ruta para poder probarse sin el árbol generado del router.
 *
 * - `GET /activation/{token}` decide qué se ve: `410` → enlace inválido (mensaje neutro, no revela
 *   si la invitación existió); cualquier otro fallo → pantalla con reintento.
 * - El token se guarda en `sessionStorage` **solo** cuando la invitación resultó válida.
 * - Tras aceptar el aviso: iOS sin instalar → `/instalar` (research R-5); el resto → `/registrar`.
 * - En escritorio no se activa nada: se muestra el enlace para abrirlo en el teléfono.
 * - 005 · QA-6: cada vez que se abre el enlace se piden los últimos 4 del documento **antes** de
 *   mostrar nada del trabajador. `GET` nunca trae el nombre; lo trae la respuesta de la
 *   verificación, que se guarda solo en el estado del componente. Excepción: `?paso=privacidad`
 *   (vuelta desde `/registrar` tras un `428`) con la invitación ya verificada va directo al aviso.
 */
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import type { ActivationInfo } from '@fermin/contracts';

import { detectPlatform, isIos, isStandalone } from '../../device/platform';
import { t } from '../../i18n';

import { setActivationToken } from './activation-store';
import { useActivationInfo } from './api';
import {
  ActivationErrorScreen,
  ActivationLoadingScreen,
  InvalidLinkScreen,
  isInvitationGone,
  isInvitationSuperseded,
} from './link-invalid';
import { PrivacyStep } from './privacy';
import { VerifyDocumentStep } from './verify-document';
import { ActivationWelcome, DesktopNotice } from './welcome';

export interface ActivationFlowProps {
  token: string;
  /** `?paso=privacidad`: entra directo al aviso (lo usa `/registrar` tras un `428`). */
  startAtPrivacy?: boolean;
}

type Step = 'verify' | 'welcome' | 'privacy';

export function ActivationFlow({ token, startAtPrivacy = false }: ActivationFlowProps) {
  const navigate = useNavigate();
  const desktop = detectPlatform() === 'desktop';
  /** Paso elegido por la persona; `null` = el inicial, que se deriva de la respuesta del `GET`. */
  const [chosenStep, setStep] = useState<Step | null>(null);
  /** Respuesta de la verificación (con el nombre de pila); solo en memoria. */
  const [verified, setVerified] = useState<ActivationInfo | null>(null);
  const [gone, setGone] = useState(false);
  const info = useActivationInfo(token, { enabled: !desktop });

  // Paso inicial: siempre la verificación, salvo `?paso=privacidad` con el enlace ya verificado.
  const alreadyVerified = info.data?.document_verification.verified ?? false;
  const step: Step = chosenStep ?? (startAtPrivacy && alreadyVerified ? 'privacy' : 'verify');

  const valid = info.data !== undefined;
  useEffect(() => {
    if (valid) {
      setActivationToken(token);
    }
  }, [valid, token]);

  const goAfterPrivacy = useCallback(() => {
    void navigate({ to: isIos() && !isStandalone() ? '/instalar' : '/registrar', replace: true });
  }, [navigate]);

  if (desktop) {
    return <DesktopNotice link={globalThis.location?.href ?? ''} />;
  }
  if (gone) {
    return <InvalidLinkScreen />;
  }
  if (info.isPending) {
    return <ActivationLoadingScreen />;
  }
  if (info.error) {
    return isInvitationGone(info.error) ? (
      <InvalidLinkScreen
        description={
          isInvitationSuperseded(info.error) ? t('activation.invalid.superseded') : undefined
        }
      />
    ) : (
      <ActivationErrorScreen message={info.error.message} onRetry={() => void info.refetch()} />
    );
  }
  if (step === 'verify') {
    return (
      <VerifyDocumentStep
        token={token}
        companyTradeName={info.data.company_trade_name}
        onVerified={(result) => {
          setVerified(result);
          setStep('welcome');
        }}
        onGone={() => setGone(true)}
      />
    );
  }
  if (step === 'welcome') {
    const current = verified ?? info.data;
    return (
      <ActivationWelcome
        info={current}
        onContinue={() =>
          current.privacy_notice.accepted ? goAfterPrivacy() : setStep('privacy')
        }
      />
    );
  }
  return (
    <PrivacyStep
      token={token}
      version={info.data.privacy_notice.version}
      onAccepted={goAfterPrivacy}
      onGone={() => void info.refetch()}
      onVerificationRequired={() => setStep('verify')}
    />
  );
}
